import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, TrendingUp, Ship, Package, Clock, AlertTriangle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const BACKEND_URL = baseURL.AiBaseURL;

const AISummaryModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);

    const fetchAISummary = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${BACKEND_URL}/api/ai/dashboard-summary`);
            setSummary(response.data);
        } catch (err) {
            console.error('Error fetching AI summary:', err);
            setError('Failed to generate AI summary. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && !summary) {
            fetchAISummary();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ai-summary-modal"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-fuchsia-500/10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">AI Dashboard Summary</h2>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Powered by GPT • Real-time insights</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={fetchAISummary}
                                    disabled={loading}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                                    title="Refresh Summary"
                                >
                                    <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                                </button>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="relative">
                                    <Loader2 className="w-12 h-12 text-violet-500 animate-spin" />
                                    <Sparkles className="w-5 h-5 text-purple-400 absolute -top-1 -right-1 animate-pulse" />
                                </div>
                                <p className="mt-4 text-slate-600 dark:text-slate-400 font-medium">Analyzing your dashboard data...</p>
                                <p className="text-sm text-slate-400 dark:text-slate-500">This may take a few seconds</p>
                            </div>
                        ) : error ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                                    <AlertTriangle className="w-8 h-8 text-red-500" />
                                </div>
                                <p className="text-slate-600 dark:text-slate-400 font-medium">{error}</p>
                                <button
                                    onClick={fetchAISummary}
                                    className="mt-4 px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : summary ? (
                            <div className="space-y-6">
                                {/* Key Metrics */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                                        <div className="flex items-center gap-2 mb-1">
                                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">On Time</span>
                                        </div>
                                        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{summary.metrics?.on_time || 0}</p>
                                    </div>
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertTriangle className="w-4 h-4 text-red-500" />
                                            <span className="text-xs text-red-600 dark:text-red-400 font-medium">Delayed</span>
                                        </div>
                                        <p className="text-2xl font-bold text-red-700 dark:text-red-300">{summary.metrics?.delayed || 0}</p>
                                    </div>
                                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Ship className="w-4 h-4 text-blue-500" />
                                            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">In Transit</span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{summary.metrics?.in_transit || 0}</p>
                                    </div>
                                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Package className="w-4 h-4 text-amber-500" />
                                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Containers</span>
                                        </div>
                                        <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{summary.metrics?.total_containers?.toLocaleString() || 0}</p>
                                    </div>
                                </div>

                                {/* AI Generated Summary */}
                                <div className="p-4 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-900/20 dark:via-purple-900/20 dark:to-fuchsia-900/20 rounded-xl border border-violet-200 dark:border-violet-800">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-lg shrink-0">
                                            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">AI Analysis</h3>
                                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                                                {summary.ai_summary}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Key Insights */}
                                {summary.insights && summary.insights.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                                            <TrendingUp className="w-4 h-4 text-violet-500" />
                                            Key Insights
                                        </h3>
                                        <div className="space-y-2">
                                            {summary.insights.map((insight, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                                                >
                                                    <div className={`p-1.5 rounded-lg ${insight.type === 'positive' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                                                        insight.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                                                            insight.type === 'negative' ? 'bg-red-100 dark:bg-red-900/30' :
                                                                'bg-blue-100 dark:bg-blue-900/30'
                                                        }`}>
                                                        {insight.type === 'positive' ? <TrendingUp className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> :
                                                            insight.type === 'warning' ? <Clock className="w-3 h-3 text-amber-600 dark:text-amber-400" /> :
                                                                insight.type === 'negative' ? <AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400" /> :
                                                                    <Ship className="w-3 h-3 text-blue-600 dark:text-blue-400" />}
                                                    </div>
                                                    <p className="text-sm text-slate-700 dark:text-slate-300 flex-1">{insight.text}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Recommendations */}
                                {summary.recommendations && summary.recommendations.length > 0 && (
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Recommendations</h3>
                                        <ul className="space-y-2">
                                            {summary.recommendations.map((rec, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                                                    <span className="text-violet-500 mt-0.5">•</span>
                                                    {rec}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default AISummaryModal;
